import React from "react";
import Section from "./Section";

export default function HTML_Links() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🔗 HTML Links</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        Links are one of the most important parts of HTML. They allow users to{" "}
        <strong>navigate between web pages</strong> or jump to sections within
        the same page. HTML uses the{" "}
        <code className="highlight_code_1">&lt;a&gt;</code> tag (anchor tag) to
        create hyperlinks.
      </p>

      {/* 🔹 Key Information Box */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#0284c7",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          🧭 Basic Syntax
        </h3>
        <p>
          The anchor tag uses the <code className="highlight_code_1">href</code>{" "}
          attribute to define the destination URL.
        </p>
        <pre className="codeExample">{`<a href="https://www.example.com">Visit Example</a>`}</pre>
        <p>
          The text between <code className="highlight_code_1">&lt;a&gt;</code>{" "}
          and <code className="highlight_code_1">&lt;/a&gt;</code> becomes
          clickable.
        </p>
      </div>

      {/* Section 1️⃣ — Basic Link */}
      <Section
        title="1️⃣ Basic HTML Link"
        html={`<a href="https://www.w3schools.com">Visit W3Schools</a>`}
        description="The <a> tag defines a hyperlink. The href attribute specifies the URL of the page the link goes to."
      />

      {/* Section 2️⃣ — Open Link in a New Tab */}
      <Section
        title="2️⃣ Open Link in a New Tab"
        html={`<a href="https://www.google.com" target="_blank">Open Google in new tab</a>`}
        description="Use the target attribute with the value _blank to open the link in a new browser tab or window."
      />

      {/* Section 3️⃣ — Link to an Email Address */}
      <Section
        title="3️⃣ Email Link"
        html={`<a href="mailto:someone@example.com">Send Email</a>`}
        description="You can use the mailto: prefix inside the href attribute to open the user's default email client."
      />

      {/* Section 4️⃣ — Link to a Phone Number */}
      <Section
        title="4️⃣ Telephone Link"
        html={`<a href="tel:+911234567890">Call Us</a>`}
        description="Use the tel: prefix to make a clickable phone link, especially useful on mobile devices."
      />

      {/* Section 5️⃣ — Link with Tooltip */}
      <Section
        title="5️⃣ Link with Title (Tooltip)"
        html={`<a href="https://www.wikipedia.org" title="Go to Wikipedia">Visit Wikipedia</a>`}
        description="The title attribute adds a tooltip text that appears when you hover over the link."
      />

      {/* Section 6️⃣ — Image as a Link */}
      <Section
        title="6️⃣ Image as a Link"
        html={`<a href="https://www.google.com" target="_blank">
  <img src="https://www.w3schools.com/html/img_girl.jpg" alt="Girl" width="150" />
</a>`}
        description="You can make an image clickable by placing it inside an anchor tag. The image then behaves as a hyperlink."
      />

      {/* Section 7️⃣ — Bookmark Links (Internal Links) */}
      <Section
        title="7️⃣ Internal Links (Bookmarks)"
        html={`<h2 id="top">Top of the Page</h2>
<p><a href="#bottom">Go to Bottom</a></p>

<p style="margin-top:500px;">This is some content...</p>

<h2 id="bottom">Bottom of the Page</h2>
<p><a href="#top">Go to Top</a></p>`}
        description="Internal links use the id attribute to jump to different sections within the same page. Use '#' followed by the id name in href."
      />

      {/* ✅ Summary Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Links</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code_1">
              &lt;a href=&quot;URL&quot;&gt;
            </code>{" "}
            defines a hyperlink.
          </li>
          <li>
            <code className="highlight_code_1">target="_blank"</code> → opens
            link in a new tab.
          </li>
          <li>
            <code className="highlight_code_1">mailto:</code> → creates email
            links.
          </li>
          <li>
            <code className="highlight_code_1">tel:</code> → creates phone call
            links.
          </li>
          <li>
            <code className="highlight_code_1">title</code> → adds tooltip text.
          </li>
          <li>
            <code className="highlight_code_1">#id</code> → links to specific
            sections on the same page.
          </li>
          <li>Images can also be used as links.</li>
        </ul>
      </div>
    </div>
  );
}
